import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icons } from '../constants';
import { searchGlobalNotes, GlobalSearchResult } from '../services/boardService';

interface GlobalSearchProps {
    onSelectResult: (boardId: string, noteId: string) => void;
}

export const GlobalSearch = ({ onSelectResult }: GlobalSearchProps) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<GlobalSearchResult[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (query.trim()) {
                setIsLoading(true);
                const result = await searchGlobalNotes(query);
                if (result.success && result.data) {
                    setResults(result.data);
                }
                setIsLoading(false);
                setSelectedIndex(0);
                setIsOpen(true);
            } else {
                setResults([]);
                setIsOpen(false);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [query]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && results[selectedIndex]) {
            e.preventDefault();
            const result = results[selectedIndex];
            onSelectResult(result.boardId, result.noteId);
            setQuery('');
            setIsOpen(false);
        } else if (e.key === 'Escape') {
            setIsOpen(false);
            setQuery('');
        }
    }, [results, selectedIndex, onSelectResult]);

    const highlightMatch = (text: string, query: string) => {
        if (!query.trim() || !text) return text;
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, i) =>
            regex.test(part) ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-500/30 rounded px-0.5 text-slate-900 dark:text-white">{part}</mark> : part
        );
    };

    const getNoteIcon = (type: string) => {
        switch (type) {
            case 'image': return <Icons.Image className="w-4 h-4" />;
            case 'list': return <Icons.List className="w-4 h-4" />;
            case 'planner': return <Icons.Calendar className="w-4 h-4" />;
            case 'table': return <Icons.Table className="w-4 h-4" />;
            default: return <Icons.Type className="w-4 h-4" />;
        }
    };


    return (
        <div ref={containerRef} className="relative w-full max-w-md z-50">
            <div className="flex items-center gap-3 bg-white dark:bg-slate-900 pl-4 pr-2 py-2 rounded-full border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group focus-within:ring-2 focus-within:ring-indigo-500/20">
                <Icons.Search className={`w-4 h-4 transition-colors ${isLoading ? 'text-indigo-500 animate-pulse' : 'text-slate-400 group-focus-within:text-indigo-500'}`} />
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search all notes..."
                    className="bg-transparent border-none outline-none text-sm font-bold w-full text-slate-700 dark:text-slate-200 placeholder:text-slate-400"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => query && setIsOpen(true)}
                />
                {query && (
                    <button
                        onClick={() => { setQuery(''); setIsOpen(false); }}
                        className="w-6 h-6 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-400"
                    >
                        <Icons.X className="w-3 h-3" />
                    </button>
                )}
                {/* <div className="w-px h-4 bg-slate-200 dark:bg-slate-800 mx-1"></div>
                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-400 select-none">⌘K</div> */}
            </div>

            {/* Results Dropdown */}
            {isOpen && query.trim() && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="p-2 max-h-[60vh] overflow-y-auto thin-scrollbar">
                        {results.length > 0 ? (
                            results.map((result, index) => (
                                <button
                                    key={`${result.boardId}-${result.noteId}`}
                                    onClick={() => {
                                        onSelectResult(result.boardId, result.noteId);
                                        setQuery('');
                                        setIsOpen(false);
                                    }}
                                    className={`w-full flex items-start gap-3 p-3 rounded-xl transition-all ${index === selectedIndex
                                        ? 'bg-indigo-50 dark:bg-indigo-900/30'
                                        : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                                        }`}
                                >
                                    <div className={`p-2 rounded-lg shrink-0 ${index === selectedIndex
                                        ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-500'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                                        }`}>
                                        {getNoteIcon(result.noteType)}
                                    </div>
                                    <div className="flex-1 text-left min-w-0">
                                        {/* Note Content Snippet as Title/Body */}
                                        <p className="font-bold text-sm text-slate-800 dark:text-slate-200 truncate">
                                            {highlightMatch(result.noteContent || 'Untitled Note', query)}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 uppercase tracking-wider max-w-[120px] truncate">
                                                {result.boardTitle}
                                            </span>
                                            <span className="text-xs text-slate-400 dark:text-slate-500 truncate">
                                                {/* Additional metadata if needed */}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            ))
                        ) : (
                            !isLoading && (
                                <div className="p-8 text-center">
                                    <Icons.Search className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                                    <p className="text-sm font-bold text-slate-500 dark:text-slate-400">No matching notes found</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Try a different search term</p>
                                </div>
                            )
                        )}
                        {isLoading && results.length === 0 && (
                            <div className="p-8 text-center">
                                <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                <p className="text-xs font-bold text-slate-400">Searching...</p>
                            </div>
                        )}
                    </div>
                    {results.length > 0 && (
                        <div className="p-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 text-center uppercase tracking-wider">
                                {results.length} result{results.length !== 1 ? 's' : ''} • ↑↓ Navigate • ↵ Select
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

